import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { HomeworkHandInSection } from '../components/homework/HomeworkHandInSection';
import { AssessmentFactsGrid } from '../components/homework/AssessmentFactsGrid';
import { HomeworkItem } from '../types';
import { statusLabel } from '../utils/assessmentDetail';

type NavigationProp = NativeStackNavigationProp<any>;

/** The shared item, plus the hand-in date a homework row may carry. */
type Homework = HomeworkItem & { submittedDate?: string };

export function HomeworkDetailScreen() {
  const route = useRoute();
  const navigation = useNavigation<NavigationProp>();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const params = route.params as { homework?: Homework } | undefined;
  const homework = params?.homework;

  if (!homework) {
    return <WebPageShell><Text>Homework not found</Text></WebPageShell>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'graded': return '#10B981';
      case 'submitted': return '#3B82F6';
      default: return '#F59E0B';
    }
  };

  const dueDate = new Date(homework.dueDate);
  const today = new Date();
  const isOverdue = dueDate < today && homework.status === 'pending';
  const isAssessment = homework.source === 'assessment';
  const badgeText = isAssessment ? statusLabel(homework) : homework.status;
  const badgeColor = isAssessment && badgeText === 'Overdue' ? '#EF4444' : getStatusColor(homework.status);

  return (
    <WebPageShell>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} accessibilityLabel="Back">
            <FeatherIcon name="arrow-left" size={24} color={colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{isAssessment ? 'Assessment Details' : 'Homework Details'}</Text>
          <View style={{ width: 24 }} />
        </View>

        <View style={[styles.card, isMobile && styles.cardMobile]}>
          <View style={[styles.cardTop, isMobile && styles.cardTopMobile]}>
            <Text style={styles.subject}>
              {isAssessment && homework.category ? `${homework.subject} · ${homework.category}` : homework.subject}
            </Text>
            <View style={[styles.badge, { backgroundColor: badgeColor }]}>
              <Text style={styles.badgeText}>{badgeText}</Text>
            </View>
          </View>

          <Text style={styles.title}>{homework.title}</Text>
          {isAssessment && homework.teacherName ? (
            <Text style={styles.setBy}>Set by {homework.teacherName}</Text>
          ) : null}

          {isAssessment ? (
            <View style={styles.factsWrap}>
              <AssessmentFactsGrid item={homework} />
            </View>
          ) : (
          <View style={styles.dates}>
            <View style={styles.dateRow}>
              <FeatherIcon name="calendar" size={16} color={colors.textMuted} />
              <View style={styles.dateInfo}>
                <Text style={styles.dateLabel}>Due Date</Text>
                <Text style={[styles.dateValue, isOverdue && styles.overdueText]}>
                  {dueDate.toLocaleDateString()}
                </Text>
              </View>
            </View>

            {homework.submittedDate && (
              <View style={styles.dateRow}>
                <FeatherIcon name="check-circle" size={16} color="#10B981" />
                <View style={styles.dateInfo}>
                  <Text style={styles.dateLabel}>Submitted</Text>
                  <Text style={styles.dateValue}>
                    {new Date(homework.submittedDate).toLocaleDateString()}
                  </Text>
                </View>
              </View>
            )}
          </View>
          )}

          <View style={styles.divider} />

          <View>
            <Text style={styles.sectionTitle}>{isAssessment ? 'Instructions' : 'Description'}</Text>
            <Text style={[styles.description, !homework.description && styles.descriptionEmpty]}>
              {homework.description || (isAssessment ? 'No instructions provided.' : 'No description provided.')}
            </Text>
          </View>

          <View style={{ height: 16 }} />

          {/* Hand in: the shared section used by the list modal too - attach
              files, note, submit, and the grade once published. */}
          <HomeworkHandInSection homeworkId={homework.id} source={homework.source} collectsUpload={homework.collectsUpload} teacherFiles={homework.teacherFiles} />
        </View>
      </ScrollView>
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  headerTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 20, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardMobile: { padding: 14 },
  cardTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  cardTopMobile: { flexWrap: 'wrap', gap: 8 } as any,
  subject: { fontSize: 12, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  badge: { paddingHorizontal: 12, paddingVertical: 4, borderRadius: 6 },
  badgeText: { fontSize: 11, color: '#FFF', fontWeight: '700', textTransform: 'capitalize' },
  title: { fontSize: 20, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  setBy: { fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginTop: -10, marginBottom: 16 },
  factsWrap: { backgroundColor: colors.background, borderRadius: 10, padding: 12, marginBottom: 4 },
  descriptionEmpty: { fontStyle: 'italic', color: colors.textMuted },
  dates: { gap: 12, marginBottom: 16 },
  dateRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  dateInfo: { flex: 1 },
  dateLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', marginBottom: 2 },
  dateValue: { fontSize: 14, color: colors.textPrimary, fontWeight: '700' },
  overdueText: { color: '#EF4444' },
  divider: { height: 1, backgroundColor: colors.border, marginVertical: 16 },
  sectionTitle: { fontSize: 14, fontWeight: '800', color: colors.textPrimary, marginBottom: 8 },
  description: { fontSize: 14, color: colors.textSecondary, lineHeight: 20 },
});
